import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance';

interface BrokerageDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  brokerId?: number | null;
}

interface BrokerageFormData {
  companyName: string;
  registrationNumber: string;
  fspNumber: string;
  physicalAddress: string;
  postalAddress: string;
  contactNumber: string;
  faxNumber: string;
  website: string;
  vatNumber: string;
  email: string;
  primaryContactPerson: string;
  bankName: string;
  accountNumber: string;
  branchCode: string;
  accountType: string;
  inceptionDate: string;
}

const emptyForm: BrokerageFormData = {
  companyName: '',
  registrationNumber: '',
  fspNumber: '',
  physicalAddress: '',
  postalAddress: '',
  contactNumber: '',
  faxNumber: '',
  website: '',
  vatNumber: '',
  email: '',
  primaryContactPerson: '',
  bankName: '',
  accountNumber: '',
  branchCode: '',
  accountType: '',
  inceptionDate: '',
};

const BrokerageDetailsModal: React.FC<BrokerageDetailsModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  brokerId,
}) => {
  const [formData, setFormData] = useState<BrokerageFormData>(emptyForm);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const fetchDetails = async () => {
      setIsLoading(true);
      setError('');
      setNotFound(false);

      try {
        const response = await axiosInstance.get('/api/broker-details', {
          params: brokerId ? { brokerId } : {},
        });
        const data = response.data?.data;

        if (!data) {
          setFormData(emptyForm);
          setNotFound(true);
          return;
        }

        setFormData({
          companyName: data.companyName || '',
          registrationNumber: data.registrationNumber || '',
          fspNumber: data.fspNumber || '',
          physicalAddress: data.physicalAddress || '',
          postalAddress: data.postalAddress || '',
          contactNumber: data.contactNumber || '',
          faxNumber: data.faxNumber || '',
          website: data.website || '',
          vatNumber: data.vatNumber || '',
          email: data.email || '',
          primaryContactPerson: data.primaryContactPerson || '',
          bankName: data.bankName || '',
          accountNumber: data.accountNumber || '',
          branchCode: data.branchCode || '',
          accountType: data.accountType || '',
          inceptionDate: data.inceptionDate ? String(data.inceptionDate).split('T')[0] : '',
        });
      } catch (err: any) {
        if (err.response?.status === 404) {
          setFormData(emptyForm);
          setNotFound(true);
        } else {
          setError(err.response?.data?.message || err.message || 'Failed to load brokerage details');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [isOpen]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');

    try {
      await axiosInstance.put(
        '/api/broker-details',
        { ...formData, brokerId: brokerId ?? undefined }
      );
      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save brokerage details');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Brokerage Details</h3>
              <p className="text-sm text-gray-600 mt-1">
                {notFound
                  ? 'Set up your brokerage profile — this appears on proposals, emails, and signed documents'
                  : 'Update your brokerage profile'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
              disabled={isSaving}
            >
              &times;
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-sm">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="py-12 text-center text-gray-500">Loading...</div>
          ) : (
            <div className="space-y-6">
              {/* Company */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3">Company Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isSaving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Primary Contact Person</label>
                    <input
                      type="text"
                      name="primaryContactPerson"
                      value={formData.primaryContactPerson}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isSaving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label>
                    <input
                      type="text"
                      name="registrationNumber"
                      value={formData.registrationNumber}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isSaving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">FSP Number</label>
                    <input
                      type="text"
                      name="fspNumber"
                      value={formData.fspNumber}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isSaving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">VAT Number</label>
                    <input
                      type="text"
                      name="vatNumber"
                      value={formData.vatNumber}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isSaving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Inception Date</label>
                    <input
                      type="date"
                      name="inceptionDate"
                      value={formData.inceptionDate}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isSaving}
                    />
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3">Contact Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isSaving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number</label>
                    <input
                      type="tel"
                      name="contactNumber"
                      value={formData.contactNumber}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isSaving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fax Number</label>
                    <input
                      type="tel"
                      name="faxNumber"
                      value={formData.faxNumber}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isSaving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      placeholder="https://"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isSaving}
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3">Addresses</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Physical Address</label>
                    <textarea
                      name="physicalAddress"
                      value={formData.physicalAddress}
                      onChange={handleChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isSaving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postal Address</label>
                    <textarea
                      name="postalAddress"
                      value={formData.postalAddress}
                      onChange={handleChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isSaving}
                    />
                  </div>
                </div>
              </div>

              {/* Banking */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3">Banking Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                    <input
                      type="text"
                      name="bankName"
                      value={formData.bankName}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isSaving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                    <input
                      type="text"
                      name="accountNumber"
                      value={formData.accountNumber}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isSaving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Branch Code</label>
                    <input
                      type="text"
                      name="branchCode"
                      value={formData.branchCode}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isSaving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
                    <select
                      name="accountType"
                      value={formData.accountType}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isSaving}
                    >
                      <option value="">Select account type</option>
                      <option value="Cheque">Cheque / Current</option>
                      <option value="Savings">Savings</option>
                      <option value="Transmission">Transmission</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 pt-6 border-t flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
            >
              {isSaving ? 'Saving...' : 'Save Brokerage Details'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BrokerageDetailsModal;